import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { AssetManagementService, AssetMetadata } from "@app/assetmanagement/services/assetmanagement-data.service";
import { IncidentLinkModel } from "../models/incident-link.model";
import { IncidentLinkService } from "./incident-link.service";

@Injectable({
    providedIn: "root",
})
export class IncidentAssetsService {
    private readonly assetManagementService = inject(AssetManagementService);

    /**
     * A links service for the assets of one incident. It lists every asset the user may view, plus the
     * linked assets whatever the user's rights, so a linked asset can always be unlinked.
     */
    public forLinkedAssets(linkedAssetIds: string[]): IncidentLinkService {
        const getAll = () => this.getLinkable(linkedAssetIds);

        return {
            getAll,
            search: (searchTerm: string) =>
                getAll().pipe(
                    map((links) =>
                        links.filter(
                            (link) =>
                                link.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                link.identifier.toString().includes(searchTerm),
                        ),
                    ),
                ),
            getByIds: (ids: string[]) =>
                this.getLinkable(ids).pipe(map((links) => links.filter((link) => ids.includes(link.id)))),
        };
    }

    private getLinkable(linkedAssetIds: string[]): Observable<IncidentLinkModel[]> {
        return this.assetManagementService
            .getLinkableAssetsVbeta({ linkedAssetIds })
            .pipe(map((assets) => assets.map((asset) => this.toLinkModel(asset))));
    }

    private toLinkModel(asset: AssetMetadata): IncidentLinkModel {
        return {
            id: asset.assetId,
            guid: asset.assetId,
            name: asset.name,
            identifier: +asset.reference,
            description: asset.description,
            status: null,
            date_Detected: null,
            date_Occurred: null,
            due_Date: null,
            displayItems: [],
        };
    }
}
